// Service worker registration tuned for "auto-update without getting stuck".
//
// The production service worker is generated by Workbox `GenerateSW`
// (see internals/webpack/webpack.prod.js) and is served at /pwa/service-worker.js
// with `skipWaiting` + `clientsClaim`. That means a new release activates and
// takes control of open tabs automatically. The job of this file is to:
//
//   1. Stop the browser/CDN from pinning an old service-worker.js script
//      (`updateViaCache: 'none'`).
//   2. Proactively check for new releases without requiring the user to close
//      every tab (`registration.update()` on load / focus / interval).
//   3. Reload the page exactly once when a NEW worker takes control, so the
//      page swaps to fresh HTML + chunks instead of crashing on a missing
//      old chunk (ChunkLoadError).

const SW_URL = '/pwa/service-worker.js';
const SW_SCOPE = '/pwa/';
const UPDATE_INTERVAL_MS = 60 * 1000;

export function register() {
	if (
		process.env.NODE_ENV !== 'production' ||
		!('serviceWorker' in navigator)
	) {
		return;
	}

	window.addEventListener('load', () => {
		// If a controller already exists, this load is an existing user. When the
		// next (new) worker takes over we reload to pick up the fresh build.
		// On the very first install there is no prior controller, so we skip that
		// initial reload to avoid a pointless double-load.
		const hadController = Boolean(navigator.serviceWorker.controller);
		let refreshing = false;

		navigator.serviceWorker.addEventListener('controllerchange', () => {
			if (refreshing || !hadController) {
				return;
			}
			refreshing = true;
			window.location.reload();
		});

		navigator.serviceWorker
			.register(SW_URL, { scope: SW_SCOPE, updateViaCache: 'none' })
			.then((registration) => {
				// A worker may already be waiting from a previous visit.
				promoteWaitingWorker(registration);

				registration.onupdatefound = () => {
					const installing = registration.installing;
					if (!installing) {
						return;
					}
					installing.onstatechange = () => {
						if (installing.state === 'installed') {
							promoteWaitingWorker(registration);
						}
					};
				};

				const checkForUpdates = () => {
					registration.update().catch(() => {});
				};

				// Catch new releases while the app stays open.
				setInterval(checkForUpdates, UPDATE_INTERVAL_MS);
				document.addEventListener('visibilitychange', () => {
					if (document.visibilityState === 'visible') {
						checkForUpdates();
					}
				});
			})
			.catch((error) => {
				console.error('SW registration failed:', error);
			});
	});
}

// Ask a waiting worker to activate immediately. The generated SW already uses
// `skipWaiting`, so this is a harmless belt-and-suspenders nudge.
function promoteWaitingWorker(registration) {
	if (registration.waiting) {
		registration.waiting.postMessage({ type: 'SKIP_WAITING' });
	}
}

export function unregister() {
	if (!('serviceWorker' in navigator)) {
		return;
	}
	navigator.serviceWorker.ready
		.then((registration) => registration.unregister())
		.catch((error) => console.error(error.message));
}
